import { useMemo } from 'react';
import { InfoCard } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { RiDatabase2Line, RiCheckLine } from '@remixicon/react';
import {
  CopyButton,
  CopyableValue,
  ExternalLink,
  InfraCodeBlock,
  InfraField,
  InfraSection,
  InfraStack,
  InfraTag,
  InfraTagValue,
  InfraTagRow,
  Text,
} from '../ui/InfraPrimitives';

export const RDSPostgresCDCInfoCard = () => {
  const { entity } = useEntity();
  const annotations = entity.metadata.annotations || {};

  const templateType = annotations['infrastructure.backstage.io/type'] || '';

  const rdsEndpoint = annotations['postgres.infrastructure/rds-endpoint'];
  const rdsAddress = annotations['postgres.infrastructure/host'];
  const rdsPort = annotations['postgres.infrastructure/port'];
  const databaseName = annotations['postgres.infrastructure/database'];
  const rdiUsername = annotations['postgres.infrastructure/user'];
  const rdiPassword = annotations['postgres.infrastructure/password'];
  const masterUsername = annotations['postgres.infrastructure/master-username'];
  const masterPassword = annotations['postgres.infrastructure/master-password'];
  const postgresVersion = annotations['postgres.infrastructure/version'];
  const connectionString = annotations['postgres.infrastructure/connection-string'];
  const psqlCommand = annotations['postgres.infrastructure/psql-command'];

  const rdsInstanceId = annotations['postgres.infrastructure/rds-instance-id'];
  const instanceClass = annotations['postgres.infrastructure/instance-class'];
  const allocatedStorage = annotations['postgres.infrastructure/allocated-storage'];
  const storageType = annotations['postgres.infrastructure/storage-type'];
  const availabilityZone = annotations['postgres.infrastructure/availability-zone'];
  const parameterGroup = annotations['postgres.infrastructure/parameter-group'];

  const cdcEnabled = annotations['postgres.infrastructure/cdc-enabled'];
  const walLevel = annotations['postgres.infrastructure/wal-level'];
  const replicationSlot = annotations['postgres.infrastructure/replication-slot'];
  const publication = annotations['postgres.infrastructure/publication'];

  const rdiConfigEncoded = annotations['postgres.infrastructure/rdi-config'];

  const consoleUrl = annotations['postgres.infrastructure/console-url'];
  const awsRegion = annotations['postgres.infrastructure/aws-region'];

  const hasRDSPostgresCDCInfo = templateType === 'aws-rds-postgres-cdc-source' && rdsEndpoint;

  if (!hasRDSPostgresCDCInfo) {
    return null;
  }

  const rdiConfig = useMemo(() => {
    if (!rdiConfigEncoded) return '';
    try {
      return window.atob(rdiConfigEncoded);
    } catch (e) {
      console.error('Failed to decode RDI config:', e);
      return '';
    }
  }, [rdiConfigEncoded]);

  const host = rdsAddress || 'Pending...';
  const port = rdsPort || '5432';
  const database = databaseName || 'rdi_source';
  const user = rdiUsername || 'rdi_user';

  const verifySql = `-- Check WAL level
SHOW wal_level;

-- Check replication slot
SELECT * FROM pg_replication_slots WHERE slot_name = '${replicationSlot || 'rdi_slot'}';

-- Check publication
SELECT * FROM pg_publication WHERE pubname = '${publication || 'rdi_publication'}';`;

  const fallbackRdiConfig = `connections:
  source:
    type: postgresql
    host: ${host}
    port: ${port}
    database: ${database}
    user: ${user}
    password: ${rdiPassword || '<your_password>'}

applier:
  target_data_type: hash

sources:
  - name: rds-postgres-source
    connector:
      class: io.debezium.connector.postgresql.PostgresConnector
      database.hostname: ${host}
      database.port: ${port}
      database.user: ${user}
      database.password: ${rdiPassword || '<your_password>'}
      database.dbname: ${database}
      database.server.name: rds_postgres
      slot.name: ${replicationSlot || 'rdi_slot'}
      publication.name: ${publication || 'rdi_publication'}
      plugin.name: pgoutput
      table.include.list: public.*`;

  const rdiConfigText = rdiConfig || fallbackRdiConfig;

  return (
    <InfoCard title="AWS RDS PostgreSQL CDC Source" icon={<RiDatabase2Line size={20} />}>
      <InfraStack>
        <InfraSection title="🗄️ RDS PostgreSQL Configuration">
          <InfraField
            label="Deployment:"
            value={<InfraTagValue>AWS RDS (Managed)</InfraTagValue>}
            mono={false}
          />
          {rdsInstanceId && (
            <InfraField label="RDS Instance ID:" value={rdsInstanceId} />
          )}
          {postgresVersion && (
            <InfraField
              label="PostgreSQL Version:"
              value={<InfraTagValue>{`PostgreSQL ${postgresVersion}`}</InfraTagValue>}
              mono={false}
            />
          )}
          {instanceClass && (
            <InfraField
              label="Instance Class:"
              value={<InfraTagValue>{instanceClass}</InfraTagValue>}
              mono={false}
            />
          )}
          {allocatedStorage && (
            <InfraField
              label="Storage:"
              value={`${allocatedStorage} GB (${storageType || 'gp2'})`}
            />
          )}
          <InfraField label="Multi-AZ:" value={<InfraTagValue>Single-AZ</InfraTagValue>} mono={false} />
          {availabilityZone && (
            <InfraField label="Availability Zone:" value={availabilityZone} />
          )}
          {awsRegion && <InfraField label="AWS Region:" value={awsRegion} />}
          {cdcEnabled === 'true' && (
            <InfraField
              label="CDC Status:"
              value={
                <InfraTagRow>
                  <InfraTag>
                    <RiCheckLine size={14} style={{ marginRight: 4 }} />
                    Enabled
                  </InfraTag>
                </InfraTagRow>
              }
              mono={false}
            />
          )}
          {walLevel && (
            <InfraField label="WAL Level:" value={<InfraTagValue>{walLevel}</InfraTagValue>} mono={false} />
          )}
          {parameterGroup && (
            <InfraField label="Parameter Group:" value={parameterGroup} />
          )}
          {consoleUrl && (
            <InfraField
              label="AWS Console:"
              value={<ExternalLink href={consoleUrl}>Open in AWS Console</ExternalLink>}
              mono={false}
            />
          )}
        </InfraSection>

        <InfraSection title="🔌 Connection Information">
          {rdsEndpoint ? (
            <CopyableValue label="RDS Endpoint:" value={rdsEndpoint} />
          ) : (
            <InfraField label="RDS Endpoint:" value="Pending..." />
          )}
          {host !== 'Pending...' ? (
            <CopyableValue label="Host:" value={host} />
          ) : (
            <InfraField label="Host:" value={host} />
          )}
          <InfraField label="Port:" value={port} />
          <InfraField label="Database:" value={database} />
          <InfraField label="RDI Username:" value={user} />
          {rdiPassword && <CopyableValue label="RDI Password:" value={rdiPassword} />}
          {masterUsername && (
            <InfraField label="Master Username:" value={masterUsername} />
          )}
          {masterPassword && (
            <CopyableValue label="Master Password:" value={masterPassword} />
          )}
          {connectionString && connectionString !== 'Pending deployment...' && (
            <>
              <InfraField label="Connection String:" value="" mono={false} />
              <div style={{ position: 'relative' }}>
                <InfraCodeBlock variant="light">{connectionString}</InfraCodeBlock>
                <div style={{ position: 'absolute', top: 4, right: 4 }}>
                  <CopyButton text={connectionString} />
                </div>
              </div>
            </>
          )}
          {psqlCommand && (
            <>
              <InfraField label="psql Command:" value="" mono={false} />
              <div style={{ position: 'relative' }}>
                <InfraCodeBlock variant="light">{psqlCommand}</InfraCodeBlock>
                <div style={{ position: 'absolute', top: 4, right: 4 }}>
                  <CopyButton text={psqlCommand} />
                </div>
              </div>
            </>
          )}
        </InfraSection>

        <InfraSection title="🔄 CDC Configuration">
          {replicationSlot && (
            <InfraField label="Replication Slot:" value={replicationSlot} />
          )}
          {publication && <InfraField label="Publication:" value={publication} />}
          <InfraField label="Plugin:" value="pgoutput" />
          <Text variant="body-medium" style={{ marginTop: '1rem', display: 'block' }}>
            Verify CDC Configuration:
          </Text>
          <div style={{ position: 'relative' }}>
            <InfraCodeBlock variant="light">{verifySql}</InfraCodeBlock>
            <div style={{ position: 'absolute', top: 4, right: 4 }}>
              <CopyButton text={verifySql} />
            </div>
          </div>
        </InfraSection>

        <InfraSection title="⚙️ Redis Data Integration (RDI) Configuration">
          <Text variant="body-medium">RDI Config Snippet:</Text>
          <div style={{ position: 'relative' }}>
            <InfraCodeBlock variant="light">{rdiConfigText}</InfraCodeBlock>
            <div style={{ position: 'absolute', top: 4, right: 4 }}>
              <CopyButton text={rdiConfigText} />
            </div>
          </div>
          <Text variant="body-small" style={{ marginTop: '0.75rem', display: 'block' }}>
            💡 Tip: This configuration is ready to use with Redis Data Integration. All passwords are included.
          </Text>
        </InfraSection>

        <InfraSection title="💡 Quick Tips">
          <Text variant="body-medium">
            • RDS is fully managed - No SSH access needed, AWS handles backups and maintenance
          </Text>
          <Text variant="body-medium">
            • Logical replication is enabled - CDC is ready to use with RDI
          </Text>
          <Text variant="body-medium">
            • Connect with psql: Use the psql command above to connect directly
          </Text>
          <Text variant="body-medium">
            • Monitor in AWS Console: Click the button above to view metrics and logs
          </Text>
          <Text variant="body-medium">
            • Free tier eligible: db.t3.micro with 20GB storage is free for 750 hours/month
          </Text>
        </InfraSection>
      </InfraStack>
    </InfoCard>
  );
};
